import React from "react";
import { Editor, RichUtils } from "draft-js";
import "draft-js/dist/Draft.css";
import {
  Bold,
  Italic,
  Underline,
  Heading2,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";

interface DraftEditorProps {
  editorState: any;
  onChange: (editorState: any) => void;
  placeholder?: string;
  readOnly?: boolean;
}

export default function DraftEditor({
  editorState,
  onChange,
  placeholder = "Ketik keterangan...",
  readOnly = false,
}: DraftEditorProps) {
  const editorRef = React.useRef<any>(null);

  const handleKeyCommand = (command: any, state: any): any => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      onChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const toggleInlineStyle = (
    e: React.MouseEvent,
    inlineStyle: "BOLD" | "ITALIC" | "UNDERLINE",
  ) => {
    e.preventDefault();
    onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const toggleBlockType = (e: React.MouseEvent, blockType: string) => {
    e.preventDefault();
    onChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const currentInlineStyle = editorState.getCurrentInlineStyle();
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  if (readOnly) {
    return (
      <div className="prose prose-sm max-w-none text-foreground">
        <Editor editorState={editorState} onChange={() => {}} readOnly={true} />
      </div>
    );
  }

  return (
    <div
      className="border rounded-lg bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all"
      onClick={() => editorRef.current?.focus()}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-1.5 border-b bg-muted/40 text-muted-foreground select-none">
        <button
          type="button"
          onClick={(e) => toggleInlineStyle(e, "BOLD")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            currentInlineStyle.has("BOLD")
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => toggleInlineStyle(e, "ITALIC")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            currentInlineStyle.has("ITALIC")
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => toggleInlineStyle(e, "UNDERLINE")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            currentInlineStyle.has("UNDERLINE")
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Underline (Ctrl+U)"
        >
          <Underline className="w-4 h-4" />
        </button>
        <div className="w-[1px] h-4 bg-border mx-1" />
        <button
          type="button"
          onClick={(e) => toggleBlockType(e, "header-two")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            blockType === "header-two"
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Heading"
        >
          <Heading2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => toggleBlockType(e, "unordered-list-item")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            blockType === "unordered-list-item"
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => toggleBlockType(e, "ordered-list-item")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            blockType === "ordered-list-item"
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={(e) => toggleBlockType(e, "blockquote")}
          className={`p-1.5 rounded hover:bg-muted transition-colors ${
            blockType === "blockquote"
              ? "bg-primary/10 text-primary font-bold"
              : ""
          }`}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Body */}
      <div className="p-3 min-h-[120px] max-h-[300px] overflow-y-auto cursor-text text-sm leading-relaxed">
        <Editor
          ref={editorRef}
          editorState={editorState}
          onChange={onChange}
          handleKeyCommand={handleKeyCommand}
          placeholder={placeholder}
          spellCheck={true}
        />
      </div>
    </div>
  );
}
